import { ReactElement } from 'react';

import styled from 'styled-components';

import { IconAiAgent } from '@components/icons/ai-agent';
import { IconCompany } from '@components/icons/company';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconIntegrations } from '@components/icons/integrations';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconList } from '@components/icons/list';
import { IconPayment } from '@components/icons/payment';
import { IconPerson } from '@components/icons/person';
import { IconPortal } from '@components/icons/portal';
import { IconTraveller } from '@components/icons/traveller';
import { IconWorkspace } from '@components/icons/workspace';
import { InfoBox } from '@components/info-box';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

type SectionIconFn = (props: { size?: number | string; color?: Color }) => ReactElement;

const SectionIcon = ({ icon: Icon }: { icon: SectionIconFn }) => (
  <Icon size={16} color="textHelper" />
);

type Permission = {
  feature: string;
  admin: boolean;
  editor: boolean;
  contributor: boolean;
};

type PermissionSection = {
  key: string;
  icon: SectionIconFn;
  permissions: Permission[];
};

const companySettingsSections: PermissionSection[] = [
  {
    key: 'companySettings',
    icon: IconCompany,
    permissions: [
      { feature: 'generalDetails', admin: true, editor: false, contributor: false },
      { feature: 'manageUsers', admin: true, editor: false, contributor: false },
      { feature: 'emailDomains', admin: true, editor: true, contributor: false },
      { feature: 'security', admin: true, editor: true, contributor: false },
    ],
  },
  {
    key: 'appsIntegrations',
    icon: IconIntegrations,
    permissions: [
      { feature: 'marketplaceApps', admin: true, editor: true, contributor: false },
      { feature: 'customIntegrations', admin: true, editor: true, contributor: false },
    ],
  },
  {
    key: 'portals',
    icon: IconPortal,
    permissions: [
      { feature: 'managePortals', admin: true, editor: true, contributor: false },
      { feature: 'viewStatistics', admin: true, editor: true, contributor: false },
    ],
  },
  {
    key: 'billing',
    icon: IconPayment,
    permissions: [
      { feature: 'paymentMethod', admin: true, editor: false, contributor: false },
      { feature: 'invoices', admin: true, editor: false, contributor: false },
      { feature: 'subscription', admin: true, editor: false, contributor: false },
    ],
  },
  {
    key: 'account',
    icon: IconPerson,
    permissions: [{ feature: 'personalApiKey', admin: true, editor: true, contributor: false }],
  },
];

const journeysSections: PermissionSection[] = [
  {
    key: 'workspaces',
    icon: IconWorkspace,
    permissions: [
      { feature: 'createManageWorkspaces', admin: true, editor: true, contributor: false },
      { feature: 'addUsersToWorkspaces', admin: true, editor: true, contributor: false },
      { feature: 'deleteWorkspaces', admin: true, editor: true, contributor: false },
    ],
  },
  {
    key: 'journeys',
    icon: IconJourneyItem,
    permissions: [
      { feature: 'addRemoveTravellersToJourneys', admin: true, editor: true, contributor: false },
      { feature: 'editTravellerJourneyData', admin: true, editor: true, contributor: true },
      {
        feature: 'startPauseResumeResetJourneys',
        admin: true,
        editor: true,
        contributor: true,
      },
    ],
  },
  {
    key: 'travellerProfilesPage',
    icon: IconTraveller,
    permissions: [
      { feature: 'viewManageTravellerProfiles', admin: true, editor: true, contributor: false },
    ],
  },
  {
    key: 'lists',
    icon: IconList,
    permissions: [
      { feature: 'viewManageLists', admin: true, editor: true, contributor: false },
      { feature: 'createSyncedLists', admin: true, editor: true, contributor: false },
    ],
  },
  {
    key: 'aiAgents',
    icon: IconAiAgent,
    permissions: [{ feature: 'viewManageAiAgents', admin: true, editor: true, contributor: false }],
  },
];

type LocaleKey = keyof typeof locale.en;

export const RolesInfoTab = () => {
  const { t } = useScopedTranslate(locale);

  return (
    <Container>
      <Stack space={6}>
        <Stack space={2}>
          <Text font="bold18" text={t('title')} />
          <Description>
            <Text font="regular14" color="textHelper" text={t('description')} />
          </Description>
        </Stack>

        {companySettingsSections.map((section) => (
          <PermissionCard key={section.key}>
            <SectionHeader>
              <SectionTitleWrapper>
                <SectionIcon icon={section.icon} />
                <SectionTitle>{t(section.key as LocaleKey)}</SectionTitle>
              </SectionTitleWrapper>
              <HeaderRole>{t('admin')}</HeaderRole>
              <HeaderRole>{t('editor')}</HeaderRole>
              <HeaderRole>{t('contributor')}</HeaderRole>
            </SectionHeader>
            {section.permissions.map((perm, index) => (
              <PermissionRow key={perm.feature} $isLast={index === section.permissions.length - 1}>
                <FeatureLabel>{t(perm.feature as LocaleKey)}</FeatureLabel>
                <PermissionCell hasPermission={perm.admin} />
                <PermissionCell hasPermission={perm.editor} />
                <PermissionCell hasPermission={perm.contributor} />
              </PermissionRow>
            ))}
          </PermissionCard>
        ))}

        <InfoBox
          variant="indigo"
          icon={<IconFormIndicatorInfo color="elementIndigo100" />}
          text={t('infoBoxText')}
          font="regular14"
        />

        <Text font="bold18" text={t('journeysSection')} />

        {journeysSections.map((section) => (
          <PermissionCard key={section.key}>
            <SectionHeader>
              <SectionTitleWrapper>
                <SectionIcon icon={section.icon} />
                <SectionTitle>{t(section.key as LocaleKey)}</SectionTitle>
              </SectionTitleWrapper>
              <HeaderRole>{t('admin')}</HeaderRole>
              <HeaderRole>{t('editor')}</HeaderRole>
              <HeaderRole>{t('contributor')}</HeaderRole>
            </SectionHeader>
            {section.permissions.map((perm, index) => (
              <PermissionRow key={perm.feature} $isLast={index === section.permissions.length - 1}>
                <FeatureLabel>{t(perm.feature as LocaleKey)}</FeatureLabel>
                <PermissionCell hasPermission={perm.admin} />
                <PermissionCell hasPermission={perm.editor} />
                <PermissionCell hasPermission={perm.contributor} />
              </PermissionRow>
            ))}
          </PermissionCard>
        ))}
      </Stack>
    </Container>
  );
};

const Container = styled.div({
  padding: pxToRem(24),
  paddingBottom: pxToRem(48),
  overflow: 'auto',
});

const Description = styled.div({
  maxWidth: pxToRem(500),
});

const PermissionCard = styled.div({
  borderRadius: pxToRem(8),
  overflow: 'hidden',
});

const SectionHeader = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr repeat(3, 80px)',
  alignItems: 'center',
  padding: `${pxToRem(12)} ${pxToRem(16)}`,
  backgroundColor: theme.color.backgroundSecondary,
});

const SectionTitleWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(8),
});

const SectionTitle = styled.span({
  font: theme.font.semibold14,
  color: theme.color.textPrimary,
});

const HeaderRole = styled.div({
  font: theme.font.semibold14,
  color: theme.color.textSecondary,
  textAlign: 'center',
});

const PermissionRow = styled.div<{ $isLast: boolean }>((p) => ({
  display: 'grid',
  gridTemplateColumns: '1fr repeat(3, 80px)',
  alignItems: 'center',
  padding: `${pxToRem(8)} ${pxToRem(16)}`,
  backgroundColor: theme.color.backgroundSecondary,
  position: 'relative',
  '&::after': p.$isLast
    ? undefined
    : {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: pxToRem(16),
        right: pxToRem(16),
        height: '1px',
        backgroundColor: theme.color.border10,
      },
}));

const FeatureLabel = styled.div({
  font: theme.font.regular14,
  color: theme.color.textPrimary,
});

const PermissionValueStyled = styled.div<{ $hasPermission: boolean }>((p) => ({
  font: theme.font.semibold14,
  color: p.$hasPermission ? theme.color.elementGreen200 : theme.color.elementRed100,
  textAlign: 'center',
}));

const PermissionCell = ({ hasPermission }: { hasPermission: boolean }) => {
  const { t } = useScopedTranslate(locale);
  return (
    <PermissionValueStyled $hasPermission={hasPermission}>
      {hasPermission ? t('core.yes') : t('core.no')}
    </PermissionValueStyled>
  );
};

const locale = {
  en: {
    title: 'Company settings',
    description:
      'Roles help set up permissions for company-level settings to help keep your organizations secure. Use these roles to control who can modify company configurations, billing, integrations and other administrative features.',
    admin: 'Admin',
    editor: 'Editor',
    contributor: 'Contributor',
    infoBoxText:
      'These roles mostly control company-level settings and navigation. Some journey actions are also affected, but most workspace and journey permissions are managed within each workspace.',
    journeysSection: 'Product access',
    companySettings: 'Company settings',
    appsIntegrations: 'Apps & Integrations',
    portals: 'Portals',
    billing: 'Subscription & billing',
    account: 'Account',
    workspaces: 'Workspaces',
    journeys: 'Journeys',
    travellerProfilesPage: 'Traveller profiles page',
    lists: 'Lists',
    aiAgents: 'AI Agents',
    generalDetails: 'General company details',
    manageUsers: 'Manage and invite members',
    emailDomains: 'Email domains',
    security: 'Security settings',
    marketplaceApps: 'Install and manage marketplace apps',
    customIntegrations: 'Create and manage custom integrations',
    managePortals: 'Create and manage company portals',
    viewStatistics: 'View portal statistics',
    paymentMethod: 'Manage payment method',
    invoices: 'View and download invoices',
    subscription: 'Manage subscription plan',
    personalApiKey: 'Can create personal API key',
    addRemoveTravellersToJourneys: 'Add and remove travellers to/from journeys',
    editTravellerJourneyData: 'Edit traveller journey data',
    startPauseResumeResetJourneys: 'Start, pause, resume and reset journeys',
    createManageWorkspaces: 'Create and manage workspaces',
    addUsersToWorkspaces: 'Add members to workspaces',
    deleteWorkspaces: 'Delete workspaces',
    viewManageTravellerProfiles: 'View and manage Traveller profiles page',
    viewManageLists: 'View and manage lists',
    createSyncedLists: 'Create and manage synced lists',
    viewManageAiAgents: 'View and manage AI agent templates',
  },
};
